// store/slices/favoritesSlice.ts
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { SaleItemInterface, SaleListResponse } from "../../common/types/types";
import { RootState } from "../store";
import { addPendingAction, SyncActionType } from "./syncSlice";

const favoritesAdapter = createEntityAdapter<SaleItemInterface>();

interface FavoritesState {
  data: ReturnType<typeof favoritesAdapter.getInitialState>;
  total: number;
  hasMore: boolean;
  loading: boolean;
}

const initialState: FavoritesState = {
  data: favoritesAdapter.getInitialState(),
  total: 0,
  hasMore: true,
  loading: false,
};

export const favoritesSlice = createSlice({
  name: "favorites",
  initialState,
  reducers: {
    setFavorites: (state, action: PayloadAction<SaleListResponse>) => {
      favoritesAdapter.setAll(state.data, action.payload.data);
      state.total = action.payload.total;
      state.hasMore = action.payload.data.length < action.payload.total;
    },
    addMoreFavorites: (state, action: PayloadAction<SaleListResponse>) => {
      const existingItems = Object.values(state.data.entities);
      const newItems = [...existingItems, ...action.payload.data];
      favoritesAdapter.setAll(state.data, newItems);
      state.total = action.payload.total;
      state.hasMore = newItems.length < action.payload.total;
    },
    updateFavoriteItem: (
      state,
      action: PayloadAction<{
        itemId: number;
        updates: Partial<SaleItemInterface>;
      }>,
    ) => {
      const { itemId, updates } = action.payload;
      if (state.data.entities[itemId]) {
        favoritesAdapter.updateOne(state.data, {
          id: itemId,
          changes: updates,
        });
      }
    },
    removeFavoriteItem: (state, action: PayloadAction<number>) => {
      favoritesAdapter.removeOne(state.data, action.payload);
      state.total = Math.max(0, state.total - 1);
    },
    addFavoriteItem: (state, action: PayloadAction<SaleItemInterface>) => {
      favoritesAdapter.addOne(state.data, action.payload);
      state.total = state.total + 1;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    clearFavorites: (state) => {
      favoritesAdapter.removeAll(state.data);
      state.total = 0;
      state.hasMore = true;
      state.loading = false;
    },
  },
});

// Экшены
export const {
  setFavorites,
  addMoreFavorites,
  updateFavoriteItem,
  removeFavoriteItem,
  addFavoriteItem,
  setLoading,
  clearFavorites,
} = favoritesSlice.actions;

// Селекторы
export const {
  selectAll: selectAllFavorites,
  selectById: selectFavoriteById,
  selectIds: selectFavoriteIds,
  selectTotal: selectTotalFavorites,
} = favoritesAdapter.getSelectors((state: RootState) => state.favorites.data);

export const selectFavoritesList = (state: RootState) =>
  selectAllFavorites(state);
export const selectFavoritesTotal = (state: RootState) => state.favorites.total;
export const selectFavoritesHasMore = (state: RootState) =>
  state.favorites.hasMore;
export const selectFavoritesLoading = (state: RootState) =>
  state.favorites.loading;

// Thunk для синхронизации избранного (будет позже)

export default favoritesSlice.reducer;
